import React from 'react';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { Button, Text, TextAs, TextSizes, ButtonHierarchies } from '@clockout-dev/design-elements';
import { ISSUE_SYNC_COPY } from '../issueTracker.constants';
import { formatRelativeAge } from '../../../utils/datetime';
import styles from '../styles/issue-sync-state.module.scss';

/**
 * Whether the caller should render the data's absence instead of the data.
 *
 * Keyed on `last_ok_at` rather than on `last_run_status === 'error'`, so "no
 * runs recorded at all" is also caught instead of falling through to a
 * reassuring empty board.
 *
 * @param {{ last_ok_at: string|null }|null|undefined} sync
 */
export function isIssueSyncUnavailable(sync) {
    return Boolean(sync) && !sync.last_ok_at;
}

/**
 * How old the mirrored data is, as a quiet line in the toolbar.
 *
 * This is the whole report, whether that age is two minutes or forty. A sync
 * that has drifted past its five-minute cadence is ordinary; dressing one as a
 * warning every time would teach people to skip past the banner that does
 * matter. `sync.stale` is therefore deliberately not consulted — the service
 * still computes it for other consumers, but here the timestamp says it all.
 *
 * @param {Object} props
 * @param {{ last_ok_at: string|null }|null} props.sync
 */
export function IssueSyncAge({ sync }) {
    if (!sync || isIssueSyncUnavailable(sync)) return null;

    return (
        <Text as={TextAs.SPAN} size={TextSizes.EXTRA_SMALL} role="status" className={styles.age}>
            {`${ISSUE_SYNC_COPY.FRESH_PREFIX} ${formatRelativeAge(sync.last_ok_at)}`}
        </Text>
    );
}

/**
 * No successful sync has ever completed.
 *
 * Rendered WHERE THE BOARD WOULD BE, not above it: an empty board means
 * "nothing matches this filter", and it must never be how a broken integration
 * looks, because that reads as "nothing is being worked on".
 *
 * @param {Object} props
 * @param {{ last_ok_at: string|null }|null} props.sync
 */
export function IssueSyncUnavailable({ sync }) {
    if (!isIssueSyncUnavailable(sync)) return null;

    return (
        <div className={styles.unavailable} role="alert">
            <ExclamationCircleFilled className={styles.icon} aria-hidden="true" />
            <div className={styles.body}>
                <Text as={TextAs.SPAN} size={TextSizes.SMALL} className={styles.title}>
                    {ISSUE_SYNC_COPY.UNAVAILABLE_TITLE}
                </Text>
                <Text as={TextAs.P} size={TextSizes.EXTRA_SMALL} className={styles.description}>
                    {ISSUE_SYNC_COPY.UNAVAILABLE_DESCRIPTION}
                </Text>
            </div>
        </div>
    );
}

/**
 * The request itself failed.
 *
 * A THIRD state, not a reuse of `IssueSyncUnavailable`. That one reports a
 * mirror we reached and found empty of successful runs; this one reports that we
 * reached nothing and therefore know nothing. Folding them together is what left
 * a failed fetch rendering an empty panel — `sync` is null on a failure, so the
 * unavailable banner silently returned null and nothing took the table's place.
 *
 * It owns the only retry on this screen because, unlike a mirror that has never
 * run, a failed request usually succeeds on the second attempt.
 *
 * @param {Object} props
 * @param {() => void} [props.onRetry]
 */
export function IssueLoadFailed({ onRetry }) {
    return (
        <div className={styles.unavailable} role="alert">
            <ExclamationCircleFilled className={styles.icon} aria-hidden="true" />
            <div className={styles.body}>
                <Text as={TextAs.SPAN} size={TextSizes.SMALL} className={styles.title}>
                    {ISSUE_SYNC_COPY.FAILED_TITLE}
                </Text>
                <Text as={TextAs.P} size={TextSizes.EXTRA_SMALL} className={styles.description}>
                    {ISSUE_SYNC_COPY.FAILED_DESCRIPTION}
                </Text>
                {onRetry ? (
                    <Button
                        hierarchy={ButtonHierarchies.LINK_COLOR}
                        className={styles.retry}
                        onClick={onRetry}
                    >
                        {ISSUE_SYNC_COPY.FAILED_RETRY}
                    </Button>
                ) : null}
            </div>
        </div>
    );
}
